import { IActivityWithAvailability } from '@/common/domain/Merge.domain';
import { TImageExcerpt } from '@/common/service/api/Activity/Activity.domain';
import { useCallback, useEffect, useState } from 'react';
import { useDateContext } from '../contexts';
import { CreateParams, splitAvailAndSoldout } from '@/entities/Attractions/service/Creators';
import { fetchUserImagesInSearch } from '@/common/service/api/Activity/Activity';
import { getAvailabilityShortByIds } from '@/entities/Attractions/service/ApiAttractionsPage';
import { mergeAvailability } from '../utils/mergeAvailability';
import { arrayHasOddItem } from '../utils/arrayHasOddItem';
import { TDate } from '../contexts/DateContext';

const CHUNK_SIZE = 20;

type TListItem = { id: string };

export const useChunksList = (items: TListItem[], id: string) => {
    const { date } = useDateContext();

    const [dataId, setDataId] = useState<string>();
    const [currentDate, setCurrentDate] = useState<TDate | undefined>(date);
    const [readyToLoad, setReadyToLoad] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [firstLoading, setFirstLoading] = useState(true);
    const [hasMoreTrips, setHasMoreTrips] = useState(false);
    const [showList, setShowList] = useState<IActivityWithAvailability[]>([]);
    const [soldOutList, setSoldOutList] = useState<IActivityWithAvailability[]>([]);
    const [userImages, setUserImages] = useState<TImageExcerpt>({} as TImageExcerpt);
    const [oddItem, setOddItem] = useState<IActivityWithAvailability[]>([]);

    /* eslint-disable sonarjs/cognitive-complexity */
    const getActivities = useCallback(async () => {
        if (isLoading) return;
        setIsLoading(true);

        const startPoint = showList.length + soldOutList.length + oddItem.length;
        const lastPoint = startPoint + CHUNK_SIZE;
        const newList = items.slice(startPoint, lastPoint);

        const alreadyLoaded = showList.length + soldOutList.length + oddItem.length;
        const totalLoadedCount = alreadyLoaded + newList.length;
        const allreadyLoadedAll = items.length <= alreadyLoaded;

        const isAllItemsLoaded = items.length <= totalLoadedCount;

        if (allreadyLoadedAll && (soldOutList.length || oddItem.length)) {
            const newItems = soldOutList.slice(0, CHUNK_SIZE);
            const rest = soldOutList.slice(CHUNK_SIZE);

            const newShowList = showList.concat(oddItem, newItems);

            const hasOdd = arrayHasOddItem(newShowList);

            if (hasOdd) {
                const oddElement = newShowList.pop();
                oddElement && setOddItem([oddElement]);
            } else {
                setOddItem([]);
            }

            setShowList(newShowList);
            setSoldOutList(rest);
            setHasMoreTrips(!!rest.length);
            setIsLoading(false);

            return;
        }

        const { activity_ids, dates } = CreateParams({ list: newList.map(({ id }) => id), date });

        if (!activity_ids) {
            setIsLoading(false);
            return;
        }

        const [newImages, availability] = await Promise.all([
            fetchUserImagesInSearch(activity_ids),
            getAvailabilityShortByIds(activity_ids, dates),
        ]);

        const newActivities = mergeAvailability(newList, availability);

        const { available, soldout } = splitAvailAndSoldout(
            newActivities as unknown as IActivityWithAvailability[]
        );

        setShowList((prev) => {
            const newItems = available.concat(oddItem);

            if (isAllItemsLoaded) {
                const fullList = newItems.concat(soldOutList, soldout);

                const itemsToShow = fullList.slice(0, CHUNK_SIZE);
                const rest = fullList.slice(CHUNK_SIZE);

                const result = prev.concat(itemsToShow);

                const hasOdd = arrayHasOddItem(result);
                const endOfList = items.length === result.length;

                if (hasOdd && !endOfList) {
                    const oddElement = result.pop();
                    oddElement && setOddItem([oddElement]);
                } else {
                    setOddItem([]);
                }
                setSoldOutList(rest);
                setHasMoreTrips(!!(rest.length || (hasOdd && !endOfList)));

                return result;
            }

            if (arrayHasOddItem(newItems)) {
                const oddElement = newItems.pop();
                oddElement && setOddItem([oddElement]);
            } else {
                setOddItem([]);
            }

            const newReuslt = prev.concat(newItems);

            setSoldOutList((prevSoldout) => prevSoldout.concat(soldout));
            setHasMoreTrips(items.length !== newReuslt.length);

            return newReuslt;
        });

        setUserImages((prev) => ({ ...prev, ...newImages }));

        setIsLoading(false);
        setFirstLoading(false);
        setReadyToLoad(false);
    }, [isLoading, showList, soldOutList, oddItem, items, date]);

    const onScrollPage = () => {
        getActivities();
    };

    useEffect(() => {
        if (id !== dataId || date?.from !== currentDate?.from) {
            setHasMoreTrips(false);
            setShowList([]);
            setSoldOutList([]);
            setOddItem([]);
            setUserImages({});
            setDataId(id);
            setReadyToLoad(true);
            setFirstLoading(true);
            setCurrentDate(date);
        }
    }, [id, dataId, date, currentDate?.from]);

    useEffect(() => {
        if (readyToLoad) {
            getActivities();
        }
    }, [getActivities, readyToLoad]);

    return {
        isLoading,
        hasMoreTrips,
        showList,
        userImages,
        onScrollPage,
        firstLoading,
    };
};
